import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import type { UserInfo } from '../../../providers/auth-provider.types';
import { resolvePostAuthTarget } from '../utils/resolve-post-auth-target';
import { pasiInscriereActive } from '../utils/pasi-inscriere';
import { verificaCodOtp } from '../api/otp.api';
import { trackOtpVerified, type FluxCod } from '@/telemetry/posthog-events';

/**
 * Intrarea in cont cu cod (T1): schimba codul pe tokenurile aplicatiei si navigheaza
 * pe EXACT aceeasi cale ca `useLoginWithForm` si `useGoogleExchange`.
 *
 * 🔴 Niciun decident nou. Redirectul post-autentificare are o singura sursa de adevar,
 * `resolvePostAuthTarget`; erau cinci decidenti independenti si un `setTimeout` castiga
 * cursa, cu profesori aterizati pe dashboard in loc de confirmare. Aici se cheama tot
 * ea, cu `pathname: null` — „tocmai a intrat, nu conteaza unde e acum".
 *
 * 🔴 NU se cheama `performRedirect()` din provider, desi pare varianta „curata":
 * `updateToken` face `setToken`, care e ASINCRON, iar `performRedirect` citeste
 * `token` din starea providerului. Chemat imediat dupa succes, ar vedea starea veche,
 * ar pica pe `isValidToken` si ar face `logout()` + navigare la login — adica exact
 * inversul a ce tocmai s-a intamplat. De-aia si login-ul, si Google-ul decodeaza
 * tokenul primit si navigheaza sincron.
 *
 * Erorile NU sunt tratate aici: `VerificareCodPage` alege textul dupa `motiv`-ul din 400
 * (`mesajEsecVerificare`: expirat, deja folosit, sau „nu se potriveste" pentru orice
 * altceva). Un `onError` aici ar fi al doilea loc care decide ce vede omul.
 */
export function useOtpVerify(flux: FluxCod = 'autentificare') {
    const { updateToken, updateRefreshToken } = useAuthProviderV2();
    const navigate = useNavigate();
    const { getUserHomePage, PATHS, PAGES, ROLES } = useRoutesProvider();

    const mutation = useMutation({
        mutationFn: ({ identificator, cod }: { identificator: string; cod: string }) =>
            verificaCodOtp(identificator, cod),
        onSuccess: (sesiune) => {
            // Ordinea e a login-ului: intai refresh-ul, apoi tokenul.
            updateRefreshToken(sesiune.refreshToken);
            updateToken(sesiune.token);

            try {
                const decoded = jwtDecode<UserInfo>(sesiune.token);
                const target =
                    resolvePostAuthTarget(decoded, null, {
                        PATHS,
                        PAGES,
                        ROLES,
                        getUserHomePage,
                        pasiInscriere: pasiInscriereActive(),
                    }) ??
                    getUserHomePage(decoded.role);
                trackOtpVerified(decoded.role, flux);
                // `replace: true`: ecranul cu codul nu are ce cauta in istoric — codul e
                // deja consumat, deci un Back ar duce la un formular care nu mai poate reusi.
                navigate(target, { replace: true });
            } catch (error) {
                console.error('Failed to decode JWT token or navigate:', error);
                navigate(getUserHomePage(), { replace: true });
            }
        },
    });

    return {
        /** Arunca la esec — pagina prinde si afiseaza mesajul potrivit motivului. */
        verifica: mutation.mutateAsync,
        seVerifica: mutation.isPending,
    };
}
